import { skipToken, useInfiniteQuery } from '@tanstack/react-query';

import { Skills } from '@air/api';
import { type Succeeded } from '@air/server-sdk';
import { FIVE_MINUTES } from '@air/utilities';
import { callAirApi } from '@air/utils-api';

import {
  toPrivateSkillsListQueryParams,
  type UsePrivateSkillsListParams,
} from '~/hooks/queries/skills/privateSkillsListQueryParams';

export type Skill = Succeeded<typeof Skills.list>['data'][number];

export type SkillScope = Skill['scope'];

export const PRIVATE_SKILLS_LIST_KEY = 'PRIVATE_SKILLS_LIST';

export type { UsePrivateSkillsListParams } from '~/hooks/queries/skills/privateSkillsListQueryParams';

export type PrivateSkillsListKey = [typeof PRIVATE_SKILLS_LIST_KEY, UsePrivateSkillsListParams];

export const getPrivateSkillsListKey = (params: UsePrivateSkillsListParams): PrivateSkillsListKey => [
  PRIVATE_SKILLS_LIST_KEY,
  toPrivateSkillsListQueryParams(params),
];

export type UsePrivateSkillsListOptions = {
  enabled?: boolean;
};

export const usePrivateSkillsList = (params: UsePrivateSkillsListParams, options?: UsePrivateSkillsListOptions) => {
  const { workspaceId } = params;
  const listParams = toPrivateSkillsListQueryParams(params);

  const queryFn = !workspaceId
    ? skipToken
    : async ({ pageParam }: { pageParam: string }) => {
        return callAirApi(() =>
          Skills.list({
            ...listParams,
            workspaceId,
            query: listParams.query || undefined,
            slashCommand: listParams.slashCommand || undefined,
            createdBy: listParams.createdBy || undefined,
            cursor: pageParam || undefined,
          }),
        );
      };

  return useInfiniteQuery({
    queryKey: getPrivateSkillsListKey(params),
    queryFn,
    getNextPageParam: (lastPage) => (lastPage.pagination?.hasMore ? (lastPage.pagination.cursor ?? null) : null),
    initialPageParam: '',
    enabled: !!workspaceId && options?.enabled,
    staleTime: FIVE_MINUTES,
  });
};
